import * as Sentry from '@sentry/react';
import { GetStateStorage, IsValidLoginAuth, IsValidSemiAuth } from 'common/storage';
import {
  LOG_DEDUPE_MAX_CACHE_SIZE,
  LOG_DEDUPE_TTL_MS,
  LOG_MESSAGE_TRUNCATE,
} from './modules';
import { resolveListgenUserID } from './resolveListgenUserID';

const SENSITIVE_KEYS = new Set([
  'password',
  'confirmPassword',
  'resetPassword',
  'oldPassword',
  'newPassword',
  'currentPassword',
  'token',
  'captcha',
  'apiKey',
  'authorization',
  'Authorization',
  'file',
  'attachments',
  'cardNumber',
  'cvc',
  'cvv',
  'expiryMonth',
  'expiryYear',
  'nameOnCard',
  'customerID',
]);

const dedupeCache = new Map();

const normalizeError = (error) => {
  if (!error) {
    return { message: 'Unknown error', stack: '' };
  }
  if (typeof error === 'string') {
    return { message: error, stack: '' };
  }
  if (error instanceof Error) {
    return {
      message: error.message || 'Unknown error',
      stack: error.stack || '',
    };
  }
  if (typeof error === 'object') {
    return {
      message: error.message || error.msg || JSON.stringify(error).slice(0, 200),
      stack: error.stack || '',
    };
  }
  return { message: String(error), stack: '' };
};

const truncateMessage = (message) => {
  const text = String(message || '');
  return text.length > LOG_MESSAGE_TRUNCATE
    ? `${text.slice(0, LOG_MESSAGE_TRUNCATE)}…`
    : text;
};

const emailDomainOnly = (email) => {
  if (!email || typeof email !== 'string') {
    return undefined;
  }
  const at = email.indexOf('@');
  return at >= 0 ? email.slice(at) : undefined;
};

const sanitizeValue = (key, value, depth = 0) => {
  if (depth > 4) {
    return '[max_depth]';
  }
  const keyLower = String(key).toLowerCase();
  if (SENSITIVE_KEYS.has(key) || SENSITIVE_KEYS.has(keyLower)) {
    return '[redacted]';
  }
  if (keyLower === 'email' || keyLower === 'emailaddress') {
    return emailDomainOnly(value) || '[redacted]';
  }
  if (value instanceof File || value instanceof Blob) {
    return '[file]';
  }
  if (Array.isArray(value)) {
    return value.map((item, i) => sanitizeValue(String(i), item, depth + 1));
  }
  if (value && typeof value === 'object') {
    const out = {};
    Object.keys(value).forEach((k) => {
      out[k] = sanitizeValue(k, value[k], depth + 1);
    });
    return out;
  }
  return value;
};

export const sanitizeLogData = (data = {}) => {
  if (!data || typeof data !== 'object') {
    return {};
  }
  return sanitizeValue('root', data);
};

const getUserContext = (options = {}) => {
  let resolvedId = null;

  if (options.listgenUserID !== undefined && options.listgenUserID !== null) {
    resolvedId =
      options.listgenUserID === 'anonymous'
        ? 'anonymous'
        : String(options.listgenUserID);
  } else {
    resolvedId = resolveListgenUserID();
  }

  const listgenUserID = resolvedId || 'N/A';

  try {
    const localState = GetStateStorage() || {};
    const userData = localState.userData || {};
    return {
      listgenUserID,
      isValidLogin: options.isValidLogin ?? IsValidLoginAuth(),
      isValidSemiAuth: options.isValidSemiAuth ?? IsValidSemiAuth(),
      hasUserData:
        options.hasUserData ??
        Boolean(userData.listgenUserID || localState.userID || resolvedId),
    };
  } catch {
    return {
      listgenUserID,
      isValidLogin: options.isValidLogin ?? false,
      isValidSemiAuth: options.isValidSemiAuth ?? false,
      hasUserData: options.hasUserData ?? Boolean(resolvedId),
    };
  }
};

const evictCacheIfNeeded = () => {
  if (dedupeCache.size <= LOG_DEDUPE_MAX_CACHE_SIZE) {
    return;
  }
  const now = Date.now();
  for (const [key, entry] of dedupeCache.entries()) {
    if (now - entry.firstSeenAt > LOG_DEDUPE_TTL_MS) {
      dedupeCache.delete(key);
    }
    if (dedupeCache.size <= LOG_DEDUPE_MAX_CACHE_SIZE) {
      break;
    }
  }
  if (dedupeCache.size > LOG_DEDUPE_MAX_CACHE_SIZE) {
    const oldestKey = dedupeCache.keys().next().value;
    if (oldestKey) {
      dedupeCache.delete(oldestKey);
    }
  }
};

const shouldSendToSentry = (dedupeKey) => {
  const now = Date.now();
  const existing = dedupeCache.get(dedupeKey);

  if (!existing || now - existing.firstSeenAt > LOG_DEDUPE_TTL_MS) {
    dedupeCache.set(dedupeKey, {
      firstSeenAt: now,
      suppressedCount: 0,
    });
    evictCacheIfNeeded();
    return { send: true, occurrenceCount: 1 };
  }

  existing.suppressedCount += 1;
  return { send: false, occurrenceCount: existing.suppressedCount + 1 };
};

const toSentryLevel = (level) => {
  switch (level) {
    case 'fatal':
    case 'error':
    case 'warning':
    case 'log':
    case 'info':
    case 'debug':
      return level;
    case 'warn':
      return 'warning';
    default:
      return 'error';
  }
};

/**
 * Track an exception to Sentry with module/code tags and a dedupe cache.
 * @param {Object} params
 * @param {string} params.module - LOG_MODULES value
 * @param {string} params.code - Stable error code (e.g. SUBMIT_FAILED)
 * @param {*} [params.error]
 * @param {Object} [params.data]
 * @param {number} [params.httpStatus]
 * @param {string} [params.level='error']
 * @param {string} [params.listgenUserID] - Use 'anonymous' for pre-auth screens
 */
export const trackException = ({
  module,
  code,
  error = null,
  data = {},
  httpStatus,
  level = 'error',
  listgenUserID: listgenUserIDOverride,
  isValidLogin,
  isValidSemiAuth,
  hasUserData,
}) => {
  if (!module || !code) {
    return;
  }

  const { message, stack } = normalizeError(error);
  const normalizedMessage = truncateMessage(message);
  const dedupeKey = `${module}:${code}:${normalizedMessage}`;
  const { send } = shouldSendToSentry(dedupeKey);

  if (!send) {
    return;
  }

  const userContext = getUserContext({
    listgenUserID: listgenUserIDOverride,
    isValidLogin,
    isValidSemiAuth,
    hasUserData,
  });

  const listgenUserID = userContext.listgenUserID;

  // Services often reject with plain strings ("OOPS!!! something went wrong."),
  // which have no stack. Wrap them in an Error so Sentry always gets a stack trace.
  const exception =
    error instanceof Error
      ? error
      : new Error(`[${module}:${code}] ${normalizedMessage}`);

  Sentry.withScope((scope) => {
    scope.setLevel(toSentryLevel(level));
    scope.setTags({
      module,
      code: String(code),
      isValidLogin: String(userContext.isValidLogin),
      isValidSemiAuth: String(userContext.isValidSemiAuth),
      hasUserData: String(userContext.hasUserData),
    });
    if (httpStatus !== undefined && httpStatus !== null) {
      scope.setTag('httpStatus', String(httpStatus));
    }
    scope.setUser({ id: listgenUserID });
    // Preserve the module:code:message grouping semantics from the Loggly setup
    scope.setFingerprint([module, String(code), normalizedMessage]);
    scope.setContext('log', {
      module,
      code: String(code),
      message: normalizedMessage,
      originalStack: stack || undefined,
      dedupeKey,
      data: sanitizeLogData({
        ...data,
        listgenUserID,
      }),
    });
    Sentry.captureException(exception);
  });
};
